/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Skeleton, Stack, Typography } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import {
  InventoryItem,
  ObtainableBundleItem,
  TargetBasketItem,
  TargetInventoryItem,
} from "store/api/generatedApi";
import { liftLoadedState } from "models/loaded";
import { InputFieldItem, InputFieldSection } from "./InputFieldItem";
import { SearchMaterials } from "contexts/search/context";
import { useMaterials } from "contexts/search/SearchContextProvider";
import { Panel } from "../types";
import {
  PeriodVariant,
  usePeriodIndex,
  usePeriodVariant,
} from "hooks/periodIndex";
import { LoadedContent } from "../../common/loading/loadedContent";
import { ValidatedTextField } from "../../common/inputs/validatedTextField";
import {
  useShowElectricalEnergyPrice,
  useShowMaterialOverrides,
} from "hooks/tenantPermissions";
import { SuppressConstraintsEditor } from "./inputEditors/suppressConstraints";
import { useIsAdmin } from "hooks/settings";
import { RoundedSolutionEditor } from "./inputEditors/roundedSolution";
import {
  useInventory,
  useMaterialChemistryOverrides,
  useObtainableBundles,
  usePeriod,
  useProductionPlan,
  useTargetBaskets,
  useTargetInventory,
} from "hooks/search";
import { ProductionPlan } from "models/ProductionPlan";

interface Props {
  setPanel: (panel: Exclude<Panel, "None" | "Constraints">) => void;
}

export const InputFields = ({ setPanel }: Props) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const formatMass = useNumberSerialiser({ decimalPlaces: 0 }).format;

  const periodTab = usePeriodIndex();
  const periodVariant = usePeriodVariant();
  const isAdmin = useIsAdmin();
  const showMaterialOverrides = useShowMaterialOverrides();

  const materials = useMaterials();
  const [inventory] = useInventory();
  const [obtainableBundles] = useObtainableBundles(periodTab);
  const [productionPlan] = useProductionPlan(periodTab);
  const [targetInventory] = useTargetInventory(periodTab);
  const [targetBaskets] = useTargetBaskets(periodTab);
  const [materialChemistryOverrides] = useMaterialChemistryOverrides();

  const getTotalNumHeats = (plan: ProductionPlan) =>
    plan.items
      .map((item) => item.num_heats)
      .reduce((a: number, b: number) => a + b, 0);
  const productionRequirementsValid = (plan: ProductionPlan) =>
    getTotalNumHeats(plan) > 0;
  const productionRequirementsSummarize = (plan: ProductionPlan) =>
    `${getTotalNumHeats(plan)} ${t("heats").toLowerCase()}`;

  const getTotalCurrentInventory = (inventory: InventoryItem[]) =>
    inventory
      .map(({ quantity }) => quantity ?? 0)
      .reduce((a: number, b: number) => a + b, 0);
  const currentInventorySummarize = (inventory: InventoryItem[]) =>
    `${formatMass(getTotalCurrentInventory(inventory))} ${units(
      "mass"
    ).trim()}`;

  const currentInventoryValid = (inventory: InventoryItem[]) => {
    const total = getTotalCurrentInventory(inventory);
    return total > 0;
  };

  const getTotalAvailableScrap = (availabilitySet: ObtainableBundleItem[]) =>
    availabilitySet
      .map((item) => item.max_obtainable ?? item.min_obtainable ?? 0)
      .reduce((left, right) => left + right, 0);

  // TODO: Is there a validation check required for prices? For min obtainable?
  // If so, add it to the `availableScrapValidator`
  const availableScrapValid = (availabilitySet: ObtainableBundleItem[]) =>
    getTotalAvailableScrap(availabilitySet) > 0;
  const availableScrapSummarize = (availabilitySet: ObtainableBundleItem[]) =>
    `${formatMass(getTotalAvailableScrap(availabilitySet))} ${units(
      "mass"
    ).trim()}`;

  const targetInventoryValid = ({
    targetInventory,
  }: {
    targetInventory: TargetInventoryItem[];
  }) =>
    targetInventory.filter(({ min_quantity, max_quantity }) =>
      max_quantity == null
        ? min_quantity != null
        : max_quantity >= (min_quantity ?? 0)
    ).length > 0;
  const targetInventorySummarize = ({
    materials,
    targetInventory,
  }: {
    materials: SearchMaterials;
    targetInventory: TargetInventoryItem[];
  }) => {
    const constrainedMaterials = targetInventory
      .filter(
        ({ min_quantity, max_quantity }) =>
          (min_quantity ?? 0) > 0 || max_quantity != null
      )
      .map(({ material_id }) => materials.byId[material_id]!.name);

    if (constrainedMaterials.length === 0) {
      return "-";
    } else if (constrainedMaterials.length <= 2) {
      return constrainedMaterials.join(", ");
    } else {
      return `${formatMass(constrainedMaterials.length)} ${t(
        "materialsLowercase"
      )}`;
    }
  };

  const targetBasketsSummarise = ({
    targetBaskets,
  }: {
    targetBaskets: TargetBasketItem[];
  }) => {
    const targetNumBaskets = targetBaskets
      .map(
        ({ chef_group_id }) =>
          targetBaskets.find((item) => item.chef_group_id === chef_group_id)
            ?.target_num_baskets ?? null
      )
      .filter((count) => count != null);

    if (targetNumBaskets.length === 0) {
      return "-";
    } else {
      const minimum = Math.min(...targetNumBaskets);
      const maximum = Math.max(...targetNumBaskets);

      return minimum === maximum
        ? formatMass(minimum)
        : `${formatMass(minimum)} - ${formatMass(maximum)}`;
    }
  };

  const showElectricalEnergyPrice = useShowElectricalEnergyPrice();

  return (
    <Stack spacing={2} sx={{ px: 3, py: 2 }}>
      <InputFieldItem
        title={t("production")}
        data={productionPlan}
        onClick={() => {
          setPanel("Production");
        }}
        valid={productionRequirementsValid}
        summarize={productionRequirementsSummarize}
      />
      {periodVariant === PeriodVariant.Schedule && (
        <InputFieldItem
          title={t("inventoryInHand")}
          data={inventory}
          onClick={() => {
            setPanel("Inventory");
          }}
          valid={currentInventoryValid}
          summarize={currentInventorySummarize}
        />
      )}
      <InputFieldItem
        title={t("availableScrap")}
        data={obtainableBundles}
        onClick={() => {
          setPanel("Availabilities");
        }}
        valid={availableScrapValid}
        summarize={availableScrapSummarize}
      />
      <InputFieldItem
        title={t("targetInventory")}
        data={liftLoadedState({ materials, targetInventory })}
        onClick={() => {
          setPanel("TargetInventory");
        }}
        valid={targetInventoryValid}
        summarize={targetInventorySummarize}
      />
      {periodVariant === PeriodVariant.Schedule && (
        <InputFieldItem
          title={t("productionBaskets")}
          data={liftLoadedState({ targetBaskets })}
          onClick={() => {
            setPanel("TargetBaskets");
          }}
          valid={() => true}
          summarize={targetBasketsSummarise}
        />
      )}
      {isAdmin || showMaterialOverrides ? (
        <InputFieldItem
          title={t("materialOverrides")}
          data={materialChemistryOverrides}
          onClick={() => {
            setPanel("MaterialOverrides");
          }}
          valid={() => true}
          summarize={(overrides) =>
            overrides.length > 0
              ? overrides.length === 1
                ? t("materialOverridesCount_singular", {
                    count: overrides.length,
                  })
                : t("materialOverridesCount_plural", {
                    count: overrides.length,
                  })
              : t("noMaterialOverrides")
          }
        />
      ) : null}
      {showElectricalEnergyPrice ? (
        <InputFieldSection title={t("electricalEnergyPrice")}>
          <ElectricalEnergyPriceEditor />
        </InputFieldSection>
      ) : null}

      {isAdmin ? (
        <>
          {periodVariant === PeriodVariant.Forecast ? (
            <InputFieldSection title={t("suppressConstraints")}>
              <SuppressConstraintsEditor periodIndex={periodTab} />
            </InputFieldSection>
          ) : null}

          <InputFieldSection title={t("optimisationObjectiveWeighting")}>
            <OptimisationObjectiveWeightingEditor />
          </InputFieldSection>
        </>
      ) : null}

      {isAdmin && periodVariant === PeriodVariant.Schedule ? (
        <InputFieldSection title={t("integerSolution")}>
          <RoundedSolutionEditor />
        </InputFieldSection>
      ) : null}
    </Stack>
  );
};

const ElectricalEnergyPriceEditor = () => {
  const periodIndex = usePeriodIndex();
  const [period, setPeriod] = usePeriod(periodIndex);
  const serialiser = useNumberSerialiser({
    min: 0,
    decimalPlaces: 2,
    default: { value: undefined, text: "" },
  });
  const units = useUnitsFormatter(false);

  return (
    <Stack direction="row" alignItems="center" sx={{ pt: 0.5 }} gap={1}>
      <LoadedContent
        data={period}
        loading={<Skeleton sx={{ width: 100, p: 0.5 }} />}
        waiting={<Skeleton sx={{ width: 100, p: 0.5 }} />}
      >
        {(loadedPeriod) => (
          // Prices in the db are per kWh, but here we want to enter as price per MWh
          <ValidatedTextField
            value={
              loadedPeriod.electrical_energy_price === undefined
                ? undefined
                : loadedPeriod.electrical_energy_price * 1000
            }
            setValue={(value) => {
              setPeriod((current) => ({
                ...current,
                electrical_energy_price: value === undefined ? 0 : value / 1000,
              }));
            }}
            serialiser={serialiser}
            textFieldProps={{
              inputProps: {
                sx: {
                  textAlign: "right",
                  p: 0.5,
                  px: 0.75,
                  width: 100,
                  fontFamily: "monospace",
                },
              },
            }}
          />
        )}
      </LoadedContent>
      <Typography variant="body1Mono">
        {units("specific_energy_cost").trim()}
      </Typography>
    </Stack>
  );
};

const OptimisationObjectiveWeightingEditor = () => {
  const [period, setPeriod] = usePeriod(usePeriodIndex());
  const serialiser = useNumberSerialiser({ min: 0, decimalPlaces: 1 });

  return (
    <Stack direction="row" alignItems="center" sx={{ pt: 0.5 }} gap={1}>
      <LoadedContent
        data={period}
        loading={<Skeleton sx={{ width: 100, p: 0.5 }} />}
        waiting={<Skeleton sx={{ width: 100, p: 0.5 }} />}
      >
        {(loadedPeriod) => (
          <ValidatedTextField
            value={loadedPeriod.optimisation_objective_weighting}
            setValue={(value) => {
              setPeriod((current) => ({
                ...current,
                optimisation_objective_weighting: value,
              }));
            }}
            serialiser={serialiser}
            textFieldProps={{
              inputProps: {
                sx: {
                  textAlign: "right",
                  p: 0.5,
                  px: 0.75,
                  width: 100,
                  fontFamily: "monospace",
                },
              },
            }}
          />
        )}
      </LoadedContent>
    </Stack>
  );
};
